import { useContext, useState } from 'react';

import { ApplicationContext } from '../../../../../../shared/source/context/context';
import { useTablePagination } from '../../../common/data-grids/common/use-table-pagination';
import { LearningOpportunitiesDataGrid } from '../../../common/data-grids/learning-opportunities/LearningOpportunitiesDataGrid';
import { useLearningOpportunitiesSearchColumns } from '../../../common/data-grids/learning-opportunities/UseLearningOpportunitiesSearchColumns';
import { useNavigationToEntity } from '../../../common/use-navigation-to-entity';
import { useGlobalSearchLearningOpportunitiesBySkillQuery } from '../../../query-hooks/global-search/learning-opportunities';

import type { LearningOpportunitySearchRow } from '../../../common/data-grids/learning-opportunities/LearningOpportunitiesDataGrid';
import type { SortOption } from '../../talent-directory/sort-option';
import type { FunctionComponent } from 'react';

export const LearningOpportunitiesBySkillTable: FunctionComponent<{
    readonly skillIds: ReadonlyArray<string>;
}> = ({ skillIds }) => {
    const { translate } = useContext(ApplicationContext);
    const { paginationModel, setPaginationModel } = useTablePagination();

    const { navigateToLearningOpportunityPage } = useNavigationToEntity();

    const [sortOptions, setSortOptions] = useState<readonly SortOption[]>([]);

    const { data, isFetching, isPending } = useGlobalSearchLearningOpportunitiesBySkillQuery({
        pagination: {
            page: paginationModel.page,
            size: paginationModel.pageSize,
        },
        skillIds,
        sortedFields: sortOptions,
    });

    const learningOpportunities = data?.data ?? [];
    const pagination = data?.pagination ?? { count: 0, page: 0, pages: 0, size: 0 };

    const noDataMessage = translate('pages.global-search.no-data.learning-opportunities');

    const rows: readonly LearningOpportunitySearchRow[] = learningOpportunities.map((learningOpportunity) => ({
        id: learningOpportunity.id,
        createdAt: learningOpportunity.createdAt,
        skills: learningOpportunity.skills,
        title: learningOpportunity.title,
    }));

    const columns = useLearningOpportunitiesSearchColumns(skillIds);

    return (
        <LearningOpportunitiesDataGrid
            columns={columns ?? []}
            isFetching={isFetching}
            isPending={isPending}
            noDataMessage={noDataMessage}
            paginationModel={paginationModel}
            rowCount={pagination.count}
            rows={rows}
            sortOptions={sortOptions}
            onPaginationModelChange={setPaginationModel}
            onRowClick={navigateToLearningOpportunityPage}
            onSortOptionsChange={setSortOptions}
        />
    );
};
